<script setup lang="ts">
import type { Language, NavItem } from "../../types/navbar";
import useTracking from "@citizenm/lobby/composables/useTracking";

defineProps<{
  navItems: NavItem[];
  languages: Language[];
  auth: {
    firstName: string;
    logout: () => void;
    loginWithRedirect: () => void;
    isAuthenticated: boolean;
  };
}>();

const { trackMainNavi } = useTracking();
const { t } = useI18n();
const localePath = useLocalePath();
</script>

<template>
  <div class="mobile-drawer">
    <ul>
      <li v-for="item in navItems">
        <LayoutCollapsable
          v-if="item.navigationItems"
          :is-open="false"
          color="dark"
          name="meetingRooms"
          @toggle="trackMainNavi(item.title)"
        >
          <template #header>
            <span class="mobile-drawer__top-link">{{ item.title }}</span>
          </template>
          <template #content>
            <nav>
              <ul class="mobile-drawer__nav-list">
                <li v-for="subItem in item.navigationItems">
                  <LayoutLink
                    class="mobile-drawer__link mobile-drawer__link--header"
                    color="dark"
                    @click="trackMainNavi(subItem.title)"
                    :link="subItem"
                  />

                  <ul>
                    <li v-for="navigationItem in subItem.navigationItems">
                      <LayoutLink
                        @click="trackMainNavi(navigationItem.title)"
                        class="mobile-drawer__header-link"
                        color="dark"
                        :link="navigationItem"
                      />

                      <ul>
                        <li v-for="link in navigationItem.navigationItems">
                          <LayoutLink
                            @click="trackMainNavi(link.title)"
                            class="mobile-drawer__link"
                            color="dark"
                            size="md"
                            :link="link"
                          />
                        </li>
                      </ul>
                    </li>
                  </ul>
                </li>
              </ul>
            </nav>
          </template>
        </LayoutCollapsable>
        <template v-else>
          <LayoutLink
            class="mobile-drawer__top-link"
            color="dark"
            :link="item"
            @click="trackMainNavi(item.title)"
          />
        </template>
      </li>
      <li>
        <hr class="mobile-drawer__divider" />

        <LayoutCollapsable
          class="mobile-drawer__language-switch"
          color="dark"
          name="languageSwitch"
          @toggle="trackMainNavi('language-selection')"
        >
          <template #header>
            <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M9 0a9 9 0 110 18A9 9 0 019 0zm3.342 9.75H9.75v6.011l.042-.053c1.535-2.008 2.38-3.992 2.55-5.958zm-8.025 0H1.914a7.132 7.132 0 004.592 5.926c-1.308-1.958-2.042-3.935-2.189-5.926zm11.769 0h-2.239c-.145 1.961-.859 3.91-2.13 5.84a7.133 7.133 0 004.369-5.84zm-7.836 0H5.822c.167 1.914.97 3.843 2.427 5.796L8.25 9.75zM6.506 2.323l-.016.007a7.132 7.132 0 00-4.576 5.92h2.403c.147-1.99.88-3.967 2.189-5.927zm3.244-.086V8.25h2.592c-.172-1.982-1.029-3.982-2.588-6.008l-.004-.005zm1.967.174l.216.337c1.137 1.82 1.778 3.656 1.914 5.503h2.239a7.131 7.131 0 00-4.369-5.84zm-3.468.044C6.792 4.408 5.99 6.337 5.822 8.25H8.25z"
                fill="currentColor"
                fill-rule="evenodd"
              />
            </svg>
          </template>
          <template #content>
            <nav>
              <LayoutLanguageSwitcher :languages="languages" />
            </nav>
          </template>
        </LayoutCollapsable>
      </li>
      <li>
        <LayoutLink
          class="mobile-drawer__link"
          color="dark"
          size="md"
          @click="trackMainNavi(t('website.navigation.booking.title'))"
          :link="{
            title: t('website.navigation.booking.title'),
            url: localePath('/get-booking'),
          }"
        />
      </li>
      <li>
        <hr class="mobile-drawer__divider" />
        <template v-if="!auth.isAuthenticated">
          <button
            class="mobile-drawer__top-link"
            @click="
              () => {
                trackMainNavi(t('website.navigation.login.title'));
                auth.loginWithRedirect();
              }
            "
          >
            {{ t("website.navigation.login.title") }}
          </button>
        </template>
        <LayoutLoggedInMenu v-else @logout="auth.logout" />
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
@use "@citizenm/foundation/assets/styles/mixins" as mixins;
@include mixins.reset-list;

.mobile-drawer {
  position: fixed;
  z-index: 20;
  width: 100vw;
  height: calc(100svh - var(--header-navigation-height-mobile, 60px));
  padding: var(--spacing-12, 48px) var(--spacing-6, 24px)
    var(--space-120, 120px);
  overflow: auto;
  background-color: var(--primary-white);
  transform-origin: top center;

  &__language-switch {
    margin-top: var(--spacing-6, 24px);
    margin-bottom: var(--spacing-6, 24px);

    &-link {
      margin-bottom: var(--spacing-4, 16px);
      font-size: 14px;
    }
  }

  &__top-link {
    margin-bottom: var(--spacing-6, 24px);
    font-weight: bold;
  }

  &__link {
    margin-bottom: var(--spacing-6, 24px);
    font-size: 14px;

    &--header {
      font-weight: bold;
      font-size: 1.2rem;
      margin-block: var(--spacing-4, 16px) 0;
    }
  }

  &__header-link {
    margin-top: var(--spacing-4, 16px);
    margin-bottom: var(--spacing-6, 24px);
    font-weight: bold;
    font-size: 14px;
  }

  &__divider {
    max-width: 4.5rem;
    margin-left: 0;
    background-color: var(--primary-black);
  }
}
</style>
